import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Checkbox } from "@headlessui/react";
import ActivityAssignee from "../ActivityAssignee";
import InputFieldDebounce from "../../components/ui/InputFieldDebounce";
import SelectItem from "../../main/component/SelectItem";
import { DatePickerField } from "../../components/ui";

const TaskForm = ({ isEditMode, eventData, onDataChange, validationErrors }) => {
  const { t } = useTranslation();

  const currentUser = useSelector((state: any) => state.userReducer.get("currentUser"));
  const currentAccountId = currentUser.get("accountId");

  useEffect(() => {
    if (!isEditMode) {
      onDataChange("assignees", currentAccountId ? [currentAccountId] : []);
    }
  }, [currentAccountId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChangeField = (fieldId, fieldValue) => {
    onDataChange(fieldId, fieldValue);
  };

  const handleSelectSearchItem = (searchItem) => {
    const item = {
      value: searchItem.get("id"),
      label: searchItem.getIn(["data", "name", "value"]),
      type: searchItem.get("type"),
    };
    handleChangeField("item", item);
    if (searchItem.get("type") !== "SALE") {
    } else {
      const contactId =
        searchItem.getIn(["data", "contact", "value", "id"]) ||
        searchItem.getIn(["data", "contact", "value", "value"]);
      handleChangeField("contactId", contactId);
    }
  };

  return (
    <div className="min-w-2xl space-y-4">
      <div>
        {isEditMode ? (
          <div className="w-full">
            <label className="block text-sm font-semibold text-gray-800 mb-2">
              {t("Task")}
              <span className="text-red-600"> *</span>
            </label>
            <div className="flex items-center gap-x-2">
              <Checkbox
                checked={eventData.done}
                onChange={(checked) => {
                  handleChangeField("done", checked);
                }}
                className="group block size-4 rounded border border-gray-300 bg-white data-checked:bg-blue-500"
              >
                <svg
                  className="stroke-white opacity-0 group-data-checked:opacity-100"
                  viewBox="0 0 14 14"
                  fill="none"
                >
                  <path
                    d="M3 8L6 11L11 3.5"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Checkbox>
              <InputFieldDebounce
                placeholder={t("Task title...")}
                value={eventData.summary}
                onChange={(value) => handleChangeField("summary", value)}
                errorMessage={validationErrors.summary}
              />
            </div>
          </div>
        ) : (
          <InputFieldDebounce
            label={t("Task")}
            required={true}
            placeholder={t("Task title...")}
            value={eventData.summary}
            onChange={(value) => handleChangeField("summary", value)}
            errorMessage={validationErrors.summary}
          />
        )}
      </div>
      <div>
        <div className="w-full">
          <label className="block text-sm font-semibold text-gray-800 mb-2">
            {t("Date")}
            <span className="text-red-600"> *</span>
          </label>
          <div className="w-full sm:w-36">
            <DatePickerField
              value={eventData.start}
              onChange={(value) => {
                handleChangeField("start", value);
                handleChangeField("end", value);
              }}
              disabledSelectDate={{ before: new Date() }}
            />
          </div>
          {validationErrors.startTimeEndTime && (
            <p className="text-xs text-red-600 mt-1">{validationErrors.startTimeEndTime}</p>
          )}
        </div>
      </div>
      <div>
        <ActivityAssignee
          title={
            <span>
              {t("Assignees")}
              <span className="text-red-600"> *</span>
            </span>
          }
          addButtonText={t("Add Assignee")}
          eventData={eventData}
          handleChange={handleChangeField}
        />
        {validationErrors.assignees && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.assignees}</p>
        )}
      </div>
      <div>
        <div className="w-full">
          <label className="block text-sm font-semibold text-gray-800 mb-2">
            {t("Linked Item")}
            <span className="text-red-600"> *</span>
          </label>
          <div className="flex items-center">
            <SelectItem
              selectedItem={eventData.item}
              onSelectItem={(searchItem) => {
                if (searchItem.get("id")) {
                  handleSelectSearchItem(searchItem);
                } else {
                  handleChangeField("item", {
                    type: searchItem.get("type"),
                  });
                }
              }}
            />
          </div>
          {validationErrors.linkItem && (
            <p className="text-xs text-red-600 mt-1">{validationErrors.linkItem}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskForm;
